import React, { useState, useCallback, memo } from 'react'
import { Container, Typography, OutsideClickHandler } from '@cleartrip/ct-design-components'
import { Stencil } from '@/components/common/Stencil'
import { DownChevronNew } from '@/assets/icons/DownChevron'
import { SortOptionType } from '@/types/srp'
import Tick from '@/assets/svg/Tick'
import { ravenSDKSrpCfwTrigger } from '@/analytics/raven/ravenSDKHelper'
import { getSrpSearchData } from '@/redux/slices/srpSlice'
import { useAppSelector } from '@/redux/store/hooks'
import { ProcessedData } from '@/utils/srp/types'

type IntlRTSortProps = {
    onwardLabel?: string
    returnLabel?: string
    isLoading?: boolean
    onClick: (selectedSorts: SortOptionType[]) => void
    activeSort: SortOptionType[]
    sortOptions: Array<SortOptionType & { price?: number; duration?: any; time?: string }>
    sortOptionsReturn: Array<SortOptionType & { price?: number; duration?: any; time?: string }>
    source?: string
    destination?: string
    processedData?: Partial<ProcessedData>
    initialTS?: number
    isRevamp?: boolean
}
// Revamp trigger: 24px chevron + 5px vertical padding + 1px border = 36px tall.
const SORT_TRIGGER_PADDING_Y_REVAMP = 5
const SORT_TRIGGER_HEIGHT_REVAMP = 36

const getSectionForKey = (sortKey: string): 'PRICE' | 'DURATION' | 'ARRIVAL' | 'DEPARTURE' => {
    if (sortKey === 'round_trip_price_up' || sortKey === 'total_price_up' || sortKey === 'price_up') return 'PRICE'
    if (sortKey === 'duration_up') return 'DURATION'
    if (sortKey === 'arrival_up' || sortKey === 'arrival_down') return 'ARRIVAL'
    return 'DEPARTURE'
}

const buildSections = (options: Array<SortOptionType & { title: string }> | undefined) => {
    const sections: { title: 'PRICE' | 'DURATION' | 'ARRIVAL' | 'DEPARTURE'; items: string[] }[] = [
        { title: 'PRICE', items: [] },
        { title: 'DURATION', items: [] },
        { title: 'ARRIVAL', items: [] },
        { title: 'DEPARTURE', items: [] }
    ]
    ;(options || []).forEach(opt => {
        const s = sections.find(sec => sec.title === getSectionForKey(opt.sortKey))
        if (s && (opt as any).title) s.items.push((opt as any).title)
    })
    return sections
}

const SortDropdown: React.FC<{
    options: Array<SortOptionType & { price?: number; duration?: any; time?: string }> | undefined
    activeSort: SortOptionType | undefined
    onSelect: (opt: SortOptionType) => void
    property: 'onward' | 'return'
    searchData?: any
    processedData?: Partial<ProcessedData>
    initialTS?: number
    isRevamp?: boolean
}> = memo(({ options, activeSort, onSelect, property, searchData, processedData, initialTS, isRevamp }) => {
    const [isOpen, setIsOpen] = useState(false)
    const [selectedKey, setSelectedKey] = useState<string>('')
    const [selectedLabel, setSelectedLabel] = useState<string>('')

    const handleToggleDropdown = useCallback(() => {
        // Raven event for sort tab click (only when opening the dropdown)
        if (!isOpen && searchData && processedData) {
            console.log("Raven Debug - processedData:", processedData)
            console.log("Raven Debug - searchData:", searchData)
            ravenSDKSrpCfwTrigger('a_ui_action', searchData, processedData, {
                action_name: 'sort_results_tab_click',
                action_type: 'click',
                property: property
            })
        }

        setIsOpen(prev => !prev)
    }, [isOpen, property, searchData, processedData])

    const sections = React.useMemo(() => buildSections(options), [options])

    const handleOptionSelect = useCallback((optionKey: string, item: string) => {
        setSelectedKey(optionKey)
        setSelectedLabel(item)
        const section = (optionKey.split('|')[0] || '') as 'PRICE' | 'DURATION' | 'ARRIVAL' | 'DEPARTURE'
        const matched = (options || []).find(o => o.title === item && getSectionForKey(o.sortKey) === section)
        
        // Raven event for sort selection (trigger before UI state changes)
        if (matched && searchData && processedData) {
            const filterType = section.toLowerCase() + '_sort' + (property === 'return' ? '_rt' : '')
            ravenSDKSrpCfwTrigger('a_sort_applied', searchData, processedData, {
                filter_type: filterType,
                filter_value: item,
                a_is_sort_applied: 'yes',
                a_quick_sort: 'no',
                a_nearby_flights: '0',
                page_loadtime: initialTS ? new Date().getTime() - initialTS : 'N/A'
            })
        }
        
        if (matched) onSelect(matched)
        setIsOpen(false)
    }, [onSelect, options, property, searchData, processedData])

    React.useEffect(() => {
        if (activeSort) {
            const section = getSectionForKey(activeSort.sortKey)
            const matched = (options || []).find(o => o.sortKey === activeSort.sortKey)
            if (matched) {
                setSelectedLabel(matched.title)
                setSelectedKey(`${section}|${matched.title}`)
            }
        } else if (options && options[0]) {
            const section = getSectionForKey(options[0].sortKey)
            setSelectedLabel(options[0].title)
            setSelectedKey(`${section}|${options[0].title}`)
        } else {
            setSelectedLabel('')
            setSelectedKey('')
        }
    }, [activeSort, options])

    const triggerClassName = isRevamp
        ? 'flex flex-middle c-pointer br-8 px-4 w-239 align-center br-8 bg-neutral-100'
        : 'flex flex-middle c-pointer br-8 h-11 px-4 w-239 align-center br-8'
    const sortByLabelColor = isRevamp ? 'subheading' : undefined
    const triggerPaddingY = isRevamp ? SORT_TRIGGER_PADDING_Y_REVAMP : undefined
    const menuTop = isRevamp ? SORT_TRIGGER_HEIGHT_REVAMP : 44

    return (
        <OutsideClickHandler onOutsideClick={() => setIsOpen(false)}>
            <Container className="p-relative" style={{ width: '239px'}} >
                <Container
                    onClick={handleToggleDropdown}
                    className={triggerClassName}
                    style={{ border: '1px solid #E7E7E7', gap: '4px', paddingTop: triggerPaddingY, paddingBottom: triggerPaddingY }}
                >
                    <Typography className="fw-400 min-w-60" color={sortByLabelColor} componentNode="span" variant="P3">Sort by:</Typography>
                    <Typography className="flex-1 ws-nowrap o-hidden fw-500" componentNode="span" variant="B3">{selectedLabel}</Typography>
                    <DownChevronNew style={{ transform: isOpen ? 'rotate(-180deg)' : 'rotate(0deg)' , marginRight: '-10px' }} />
                </Container>
                {isOpen && (
                    <Container
                        className="br-12 p-absolute bg-white z-30 o-hidden mt-1"
                        style={{ boxShadow: '0px 8px 24px rgba(26, 26, 26, 0.1)', top: menuTop, right: 0, transform: 'translateX(4px)' }}
                    >
                        {sections.map((section, sectionIndex) => (
                            <Container key={section.title}>
                                <Typography
                                    variant="B4"
                                    className='px-4 pt-2 pb-1 fw-500'
                                    style={{ color: '#6B7280' }}
                                >
                                    {section.title}
                                </Typography>
                                {section.items.map((item) => {
                                    const optionKey = `${section.title}|${item}`
                                    const isSelected = selectedKey === optionKey
                                    return (
                                        <Container
                                            key={optionKey}
                                            className="fs-3 fw-500 py-3 px-4 h-11 sortOption flex flex-middle flex-between "
                                            onClick={() => handleOptionSelect(optionKey, item)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <Typography componentNode="span" variant="B2">{item}</Typography>
                                            {isSelected ? <Tick /> : null}
                                        </Container>
                                    )
                                })}
                                {sectionIndex !== sections.length - 1 ? (
                                    <Container style={{ width: 258, height: 0, borderTop: '1px solid #E7E7E6' }} />
                                ) : null}
                            </Container>
                        ))}
                    </Container>
                )}
            </Container>
        </OutsideClickHandler>
    )
})

const IntlRTSort: React.FC<IntlRTSortProps> = memo(({
    onwardLabel = '',
    returnLabel = '',
    isLoading = false,
    onClick,
    activeSort,
    sortOptions,
    sortOptionsReturn,
    source,
    destination,
    processedData,
    initialTS,
    isRevamp = false
}) => {
    const { searchData } = useAppSelector(getSrpSearchData)
    const legHeaderVariant = isRevamp ? 'L1' : 'L2'
    if (isLoading) {
        return (
            <Container className="flex">
                <Container className="flex h-11 gap-21 flex-between flex-middle p-relative w-50p pr-2">
                    <Stencil shimmer height="14px" width="120px" borderRadius="8px" />
                    <Stencil shimmer height="44px" width="239px" borderRadius="12px" />
                </Container>
                <Container className="flex gap-21 h-11 flex-between flex-middle p-relative w-50p pl-3">
                    <Stencil shimmer height="14px" width="120px" borderRadius="12px" />
                    <Stencil shimmer height="44px" width="239px" borderRadius="12px" />
                </Container>
            </Container>
        )
    }
    
    return (
        <Container className="flex ">
            <Container className="flex h-11 gap-21 flex-between flex-middle p-relative w-50p px-2 ">
                <Typography variant={legHeaderVariant}>Onward {source || onwardLabel} {'→'} {destination || returnLabel}</Typography>
                <SortDropdown 
                    options={sortOptions || []} 
                    activeSort={activeSort?.[0]} 
                    onSelect={(opt) => onClick([opt, activeSort?.[1]])} 
                    property="onward"
                    isRevamp={isRevamp}
                    searchData={searchData}
                    processedData={processedData}
                    initialTS={initialTS}
                />
            </Container>
            <Container className="flex gap-21 h-11 flex-between flex-middle p-relative w-50p">
                <Typography className='ml-3' variant={legHeaderVariant}>Return {destination || returnLabel} {'→'} {source || onwardLabel}</Typography>
                <SortDropdown 
                    options={sortOptionsReturn || []} 
                    activeSort={activeSort?.[1]} 
                    onSelect={(opt) => onClick([activeSort?.[0], opt])} 
                    property="return"
                    isRevamp={isRevamp}
                    searchData={searchData}
                    processedData={processedData}
                    initialTS={initialTS}
                />
            </Container>
        </Container>
    )
})

SortDropdown.displayName = 'SortDropdown'

export default IntlRTSort


